import { Injectable, Injector } from '@angular/core';
import { NG1_Q, NG1_UIB_MODAL } from '@commons/core/core.provider.ajs';
import { RxUtil } from '@commons/util/utilities/rx';
import { IPromise } from 'angular';
import { filter, fromEvent, map, mergeMap, Observable, of, Subject, timer } from 'rxjs';

import { Logger, Option } from '@contact/upgrade';

import { IModalEvent, ModalEvent } from './models/modal-event';

const SNAKE_SEPARATOR = '-';
const SNAKE_REGEXP = /[A-Z]/g;

/**
 * Modal service
 */
@Injectable({
  providedIn: 'root',
})
export class ModalService {
  private modalParam: any;
  private modalInstance: any;
  private modalIsReduced: boolean;

  private title: string;

  private promise: IPromise<string> | undefined;
  private subject: Subject<IModalEvent>;

  constructor(private readonly logger: Logger, private readonly injector: Injector) {
    this.logger = this.logger.forSource(`ModalService`);

    this.modalIsReduced = false;
    this.subject = new Subject<IModalEvent>();
  }

  isModalReduced(): boolean {
    return this.modalIsReduced;
  }

  open(modalParam: any): Observable<IModalEvent> {
    this.logger.info(`Open modal with component name [${modalParam.component}-${modalParam.modalId}]`);
    this.promise = this.promise || this.injector.get(NG1_Q).resolve('');
    let result$: Observable<string> = RxUtil.fromPromise(() => this.promise!).pipe(
      mergeMap((id: string) => {
        if (id === '') {
          return of(true);
        } else if (id === modalParam.modalId) {
          return of(false);
        } else if (this.modalInstance) {
          this.modalInstance.close();
          return RxUtil.fromPromise(() => this.modalInstance.closed).pipe(map(() => true));
        } else {
          return of(true);
        }
      }),
      mergeMap((shouldOpen: boolean) => {
        if (shouldOpen) {
          this.title = document.title;
          document.title = modalParam.title;

          this.modalIsReduced = false;
          this.modalParam = modalParam;
          return RxUtil.fromPromise(() => this.modal(modalParam)).pipe(map(() => modalParam.modalId));
        } else {
          this.unreduce();
          return of(modalParam.component);
        }
      })
    );

    this.promise = RxUtil.toPromise(result$, this.injector.get(NG1_Q));
    return this.subject.asObservable().pipe(filter(event => event.component === modalParam.component));
  }

  close(): IPromise<any> {
    this.logger.info(`Close modal with component name [${this.modalParam.component}]`);
    this.promise = this.injector.get(NG1_Q).resolve('');
    this.modalInstance.close('Modal will be closed');
    return this.modalInstance.result;
  }

  dismiss(): void {
    this.logger.info(`dismiss modal with component name [${this.modalParam.component}]`);
    this.promise = this.injector.get(NG1_Q).resolve('');
    this.modalInstance.close('Modal will be closed');
  }

  reduce() {
    document.title = this.title;

    Option.of(document.getElementsByTagName(this.toSnakeCase(this.modalParam.component)))
      .map(el => $(el))
      .sideEffect(jEl => {
        this.logger.info(`Reduce modal with component name [${this.modalParam.component}]`);
        this.modalIsReduced = true;
        // Add REDUCED class
        jEl.parents('.modal').addClass('reduced');
        this.sendEventAfterAnimation(ModalEvent.reduce(this.modalParam.component));
      })
      .orSideEffect(() => this.logger.warn(`Can't reduce cause modal not found with component name [${this.modalParam.component}]`));
  }

  unreduce() {
    document.title = this.modalParam.title;

    Option.of(document.getElementsByTagName(this.toSnakeCase(this.modalParam.component)))
      .map(el => $(el))
      .sideEffect(jEl => {
        this.logger.info(`Unreduce modal with component name [${this.modalParam.component}]`);
        this.modalIsReduced = false;
        // Remove REDUCED class
        jEl.parents('.modal').removeClass('reduced');
        this.sendEventAfterAnimation(ModalEvent.unreduce(this.modalParam.component));
      })
      .orSideEffect(() => this.logger.warn(`Can't unreduce cause modal not found with component name [${this.modalParam.component}]`));
  }

  private toSnakeCase(name: string): string {
    return name.replace(SNAKE_REGEXP, (character, pos) => (pos ? SNAKE_SEPARATOR : '') + character.toLowerCase());
  }

  private modal(modalParam: any): IPromise<void> {
    this.modalInstance = this.injector.get(NG1_UIB_MODAL).open(modalParam);
    this.modalInstance.result.then(() => this.sendEventAfterAnimation(ModalEvent.close(modalParam.component)));
    this.modalInstance.result.catch(() => {
      this.dismiss();
      return this.sendEventAfterAnimation(ModalEvent.dismiss(modalParam.component));
    });

    return this.modalInstance.rendered.then(() => {
      this.sendEventAfterAnimation(ModalEvent.load(modalParam.component), 0);

      // Fullscreen
      if (modalParam.fullscreen) {
        let el = document.getElementsByTagName(this.toSnakeCase(modalParam.component));
        // Remove backdrop overlay
        $(el).parents('.modal').addClass('new_modal fullscreen');
      }

      // Mini by keyboard
      if (modalParam.ctrlKeyboard) {
        let disposable = of(window.document)
          .pipe(mergeMap(doc => fromEvent<KeyboardEvent>(doc, 'keydown')))
          .subscribe(event => {
            if (event.defaultPrevented) return;
            if (event.ctrlKey) {
              switch (event.keyCode) {
                case 38:
                  this.unreduce();
                  break;
                case 40:
                  this.reduce();
                  break;
                case 46:
                  this.close();
                  break;
                default:
                  break;
              }
            }
          });

        this.modalInstance.result.then(() => {
          document.title = this.title;
          this.title = '';
          Option.of(disposable).forEach(d => d.unsubscribe());
        });
      }
    });
  }

  // launch after animation
  // Delay prevent laggy modal animations
  private sendEventAfterAnimation(event: ModalEvent, delay: number = 800): void {
    timer(delay)
      .pipe(map(() => event))
      .subscribe(next => this.subject.next(next));
  }
}
